export const FORGELINK_FLOW_AREAS = {
  concept: ["concept"],
  design: ["concept", "design"],
  render: ["design"],
  ai_studio: ["design", "prototype"],
  orca: ["design", "prototype"],
  orca_bridge: ["design", "prototype"],
  customize: ["design", "prototype"],
  gcode: ["design", "prototype", "queue", "produce"],
  prototype: ["prototype"],
  recipe: ["recipe"],
  produce: ["produce", "queue", "materials"],
  qa: ["qa"],
  ready: ["ready", "ready_for_store"],
};

export const DEFAULT_CUSTOMIZE_NOTES =
  "Founder low-waste purge: flush matrix off-diagonal entries are 0, flush multiplier is 1, and the flush vector is 0. Purge goes into support and inner infill first; only the remainder goes to a narrow prime tower. Applied as OrcaSlicer project settings on the model handoff.";

export const DEFAULT_PURGE_PROCESS_NOTES =
  `${DEFAULT_CUSTOMIZE_NOTES} SD-card first; live printer-farm upload stays parked.`;

export const DEFAULT_TRANSITION_TARGETS = ["support", "infill", "asymmetric_tower"];

export const CUSTOMIZE_PROCESSOR_PENDING = "pending_founder_recipe";

export const ORCA_BRIDGE_CHECKED = "CHECKED";

export const ORCA_BRIDGE_CHECKED_MEANING =
  "CHECKED means package checks passed, not physical printer validation.";

export const ORCA_BRIDGE_PURGE_NOTE =
  "purge_reduction_applied stays false. This check does not reduce purge, delete sweeps, or rewrite toolpaths. The founder low-waste purge recipe is Orca project settings on the model handoff, not this check.";

export const DEFAULT_PLATE_NOTES =
  "Confirm the physical plate type matches the slicer plate selection before slicing. PETG adhesion is a known founder gate; do not treat a slice estimate as a successful print.";

export function designArtifact(record) {
  return record?.metadata?.design_artifact || null;
}

export function meshSessionSource(record) {
  const source = record?.metadata?.session_source;
  if (!source || source.kind !== "mesh" || !source.artifact_id) return null;
  return source;
}

export function printLoop(record) {
  const loop = record?.metadata?.print_loop;
  return loop && typeof loop === "object" ? loop : {};
}

export function aiStudioPackage(record) {
  return printLoop(record).ai_studio || null;
}

export function studioReturns(record) {
  const rows = printLoop(record).studio_returns;
  return Array.isArray(rows) ? rows : [];
}

export function latestStudioReturn(record) {
  const rows = studioReturns(record);
  return rows.length ? rows[rows.length - 1] : null;
}

export function orcaHandoff(record) {
  return printLoop(record).orca || null;
}

export function orcaBridgeRuns(record) {
  const rows = printLoop(record).orca_bridge_runs;
  return Array.isArray(rows) ? rows : [];
}

export function latestOrcaBridge(record) {
  const rows = orcaBridgeRuns(record);
  return rows.length ? rows[rows.length - 1] : null;
}

export function orcaBridgeChecked(record) {
  return latestOrcaBridge(record)?.status === ORCA_BRIDGE_CHECKED;
}

export function gcodeDrops(record) {
  const rows = printLoop(record).gcode_drops;
  return Array.isArray(rows) ? rows : [];
}

export function customizeRecipe(record) {
  const recipe = printLoop(record).customize_recipe;
  return recipe && typeof recipe === "object" ? recipe : null;
}

export function customizeRuns(record) {
  const rows = printLoop(record).customize_runs;
  return Array.isArray(rows) ? rows : [];
}

export function latestCustomize(record) {
  const rows = customizeRuns(record);
  return rows.length ? rows[rows.length - 1] : null;
}

export function customizeIsSdReady(record) {
  return Boolean(latestCustomize(record)?.sd_ready);
}

export function loopSdReady(record) {
  return gcodeDrops(record).length > 0 || customizeIsSdReady(record);
}

export function loopNeedsStudioReturn(record) {
  const pack = aiStudioPackage(record);
  if (!pack) return false;
  const ret = latestStudioReturn(record);
  if (!ret) return true;
  if (ret.source_package_id && pack.id && ret.source_package_id !== pack.id) return true;
  const packAt = pack.exported_at || "";
  const retAt = ret.imported_at || "";
  return Boolean(packAt && retAt && packAt > retAt);
}

export function recordsForFlowStep(records, stepKey) {
  const rows = records || [];
  if (stepKey === "render") return rows.filter((row) => designArtifact(row) || generations(row).length);
  if (stepKey === "design") return rows.filter((row) => ["concept", "design"].includes(row.area) || designArtifact(row) || generations(row).length || meshSessionSource(row));
  if (stepKey === "ai_studio") return rows.filter((row) => designArtifact(row) || generations(row).length || aiStudioPackage(row));
  if (stepKey === "orca") return rows.filter((row) => latestStudioReturn(row) || orcaHandoff(row));
  if (stepKey === "orca_bridge") return rows.filter((row) => orcaHandoff(row)?.files?.length || orcaBridgeRuns(row).length);
  if (stepKey === "customize") return rows.filter((row) => orcaHandoff(row)?.files?.length || customizeRuns(row).length || customizeRecipe(row));
  if (stepKey === "gcode") return rows.filter((row) => orcaHandoff(row) || gcodeDrops(row).length || customizeIsSdReady(row));
  const areas = FORGELINK_FLOW_AREAS[stepKey];
  if (!areas) return rows;
  return rows.filter((row) => areas.includes(row.area));
}

export function pathwayLabel(record) {
  if (gcodeDrops(record).length) return "SD-ready";
  const custom = latestCustomize(record);
  if (custom?.sd_ready) {
    return custom.processor === CUSTOMIZE_PROCESSOR_PENDING ? "SD-ready · recipe pending" : "SD-ready";
  }
  if (orcaBridgeChecked(record)) return "Orca Bridge checked";
  if (orcaHandoff(record)?.files?.length) return "Orca handoff";
  if (loopNeedsStudioReturn(record)) return "awaiting Studio return";
  if (aiStudioPackage(record)) return "AI Studio package";
  const artifact = designArtifact(record);
  if (artifact?.previewable) return "render ready";
  if (artifact) return "design attached";
  if (record?.area === "design") return "needs design file";
  if (record?.area === "concept") return "idea";
  return (record?.area || "record").replace(/_/g, " ");
}

export function isImageArtifact(artifact) {
  return Boolean(artifact?.previewable || artifact?.kind === "image" || String(artifact?.content_type || "").startsWith("image/"));
}

export function pathwayStage(record) {
  return record?.metadata?.pathway_stage || record?.area || "concept";
}

export function generations(record) {
  const rows = record?.metadata?.generations;
  return Array.isArray(rows) ? rows : [];
}

export function currentGenerationId(record) {
  return record?.metadata?.current_generation_id || record?.metadata?.design_artifact?.generation_id || null;
}

export function referenceGenerationId(record) {
  return record?.metadata?.reference_generation_id || null;
}

export function isReferenceGeneration(row) {
  return Boolean(row && (row.mode === "reference" || row.renderer === "upload"));
}

export function deskIterations(record) {
  return generations(record).filter((row) => !isReferenceGeneration(row));
}

function rowsById(record) {
  return new Map(generations(record).map((row) => [row.id, row]));
}

function listedIds(record, listKey, singularKey) {
  const meta = record?.metadata || {};
  if (Array.isArray(meta[listKey])) return meta[listKey].filter((id) => typeof id === "string" && id);
  const single = meta[singularKey];
  return single ? [single] : [];
}

export function deskSources(record) {
  const meta = record?.metadata || {};
  const byId = rowsById(record);
  if (Array.isArray(meta.source_generation_ids)) {
    return meta.source_generation_ids.map((id) => byId.get(id)).filter(Boolean);
  }
  // Older desks stored one uploaded image in the combined source/reference slot.
  if (Array.isArray(meta.reference_generation_ids)) return [];
  const legacy = byId.get(meta.reference_generation_id);
  return legacy && isReferenceGeneration(legacy) ? [legacy] : [];
}

export function deskReferences(record) {
  const byId = rowsById(record);
  return listedIds(record, "reference_generation_ids", "reference_generation_id")
    .map((id) => byId.get(id))
    .filter(Boolean);
}

export function deskReference(record) {
  const listed = deskReferences(record);
  if (listed.length) return listed[0];
  if (Array.isArray(record?.metadata?.reference_generation_ids)) return null;
  const rows = generations(record);
  const id = referenceGenerationId(record);
  if (id) {
    const match = rows.find((row) => row.id === id);
    if (match) return match;
  }
  return rows.find(isReferenceGeneration) || null;
}

export const DESK_STYLE_PRESETS = [
  { id: "product-photo", label: "Product photo", prompt: "product photo" },
  { id: "flat-mockup", label: "Flat mockup", prompt: "flat mockup" },
  { id: "studio-lighting", label: "Studio lighting", prompt: "studio lighting" },
  { id: "3d-render", label: "3D render", prompt: "3D render" },
  { id: "sketch", label: "Sketch", prompt: "sketch" },
  { id: "lifestyle", label: "Lifestyle", prompt: "lifestyle" },
];

export const DESK_STYLE_CUSTOM = "custom";

export function resolveDeskStyleChoice(style) {
  const value = String(style || "").trim();
  if (!value) return { presetId: "", custom: "" };
  const match = DESK_STYLE_PRESETS.find((row) => row.prompt.toLowerCase() === value.toLowerCase());
  if (match) return { presetId: match.id, custom: "" };
  return { presetId: DESK_STYLE_CUSTOM, custom: value };
}

export function deskStyleValue(presetId, custom) {
  if (presetId === DESK_STYLE_CUSTOM) return String(custom || "").trim();
  return DESK_STYLE_PRESETS.find((row) => row.id === presetId)?.prompt || "";
}

export function canContinueEditing(selected, reference, sources) {
  const usable = (row) => Boolean(row?.id && (row?.artifact || isReferenceGeneration(row)));
  if (usable(selected) || usable(reference)) return true;
  return Array.isArray(sources) && sources.some(usable);
}

export function iterationScore(row) {
  const value = Number(row?.score);
  return Number.isInteger(value) && value >= 1 && value <= 5 ? value : 0;
}

export function iterationKindLabel(row) {
  if (!row) return "";
  if (row.mode === "reference" || row.renderer === "upload") return "reference";
  if (row.mode === "edit") return "edit";
  return "";
}

export function buildDeskGenerationPayload({ prompt, mode = "generate", parentId, textOverlay, style, sourceIds, referenceIds }) {
  const body = {
    prompt: String(prompt || "").trim(),
    mode: mode === "edit" ? "edit" : "generate",
  };
  if (body.mode === "edit" && parentId) body.parent_id = parentId;
  if (String(textOverlay || "").trim()) body.text_overlay = String(textOverlay).trim();
  if (String(style || "").trim()) body.style = String(style).trim();
  const sources = Array.isArray(sourceIds) ? sourceIds.filter(Boolean) : [];
  const references = Array.isArray(referenceIds) ? referenceIds.filter(Boolean) : [];
  if (sources.length) body.source_ids = sources;
  if (references.length) body.reference_ids = references;
  return body;
}

const FORGE_ALIAS_PREFIX = /^\/(?:lincforge|forgelinc)(?=\/|$)/;

/** Product routes are `/linkforge` and Design Desk under that tree. Alias prefixes are not routes. */
export function canonicalLinkforgePath(value) {
  const raw = String(value || "");
  if (!raw.startsWith("/") || raw.startsWith("//")) return raw;
  const hashAt = raw.indexOf("#");
  const hash = hashAt >= 0 ? raw.slice(hashAt) : "";
  const beforeHash = hashAt >= 0 ? raw.slice(0, hashAt) : raw;
  const queryAt = beforeHash.indexOf("?");
  const query = queryAt >= 0 ? beforeHash.slice(queryAt) : "";
  const path = queryAt >= 0 ? beforeHash.slice(0, queryAt) : beforeHash;
  if (!FORGE_ALIAS_PREFIX.test(path)) return raw;
  return `${path.replace(/^\/(?:lincforge|forgelinc)/, "/linkforge")}${query}${hash}`;
}

export function designDeskPath(recordId) {
  return recordId ? `/linkforge/design?record=${encodeURIComponent(recordId)}` : "/linkforge/design";
}

export function loopHubPath(recordId, action) {
  const params = new URLSearchParams();
  if (recordId) params.set("record", recordId);
  if (action) params.set("loop", action);
  const query = params.toString();
  return query ? `/linkforge?${query}` : "/linkforge";
}

export function hubProductPath(recordId, { view, loop } = {}) {
  const params = new URLSearchParams();
  if (recordId) params.set("record", recordId);
  if (view) params.set("view", view);
  if (loop) params.set("loop", loop);
  const query = params.toString();
  return query ? `/linkforge?${query}` : "/linkforge";
}

export const PIPELINE_STAGES = [
  { id: "idea", label: "Idea" },
  { id: "design", label: "Design" },
  { id: "render", label: "Render" },
  { id: "prototype", label: "Prototype" },
  { id: "make", label: "Make" },
];

export function pipelineStage(record) {
  if (!record) return "idea";
  const chosen = record?.metadata?.pipeline_stage;
  if (PIPELINE_STAGES.some((row) => row.id === chosen)) return chosen;
  if (
    loopSdReady(record)
    || record.status === "store_ready"
    || ["produce", "queue", "qa", "ready_for_store", "ready"].includes(record.area)
  ) return "make";
  if (
    record.area === "prototype"
    || aiStudioPackage(record)
    || latestStudioReturn(record)
    || orcaHandoff(record)
    || orcaBridgeRuns(record).length
    || customizeRuns(record).length
  ) return "prototype";
  const hasDesign = Boolean(designArtifact(record)) || generations(record).length > 0 || Boolean(meshSessionSource(record));
  if (!hasDesign) return "idea";
  const stage = pathwayStage(record);
  if (stage === "render" || PAST_RENDER_STAGES.has(stage)) return "render";
  return "design";
}

export function pipelineStageLabel(stageId) {
  return PIPELINE_STAGES.find((row) => row.id === stageId)?.label || "Idea";
}

const WORK_STATUS_LABELS = {
  blocked: "Blocked",
  qa: "Needs review",
  store_ready: "Ready",
  ready: "Ready",
  complete: "Ready",
  in_progress: "In progress",
  planned: "Planned",
};

export function workStatusLabel(record) {
  const status = String(record?.status || "").toLowerCase();
  if (WORK_STATUS_LABELS[status]) return WORK_STATUS_LABELS[status];
  const stage = pipelineStage(record);
  if (stage === "make") return "Ready";
  if (stage === "idea") return "Planned";
  return "In progress";
}

export function workStatusTone(record) {
  const label = workStatusLabel(record);
  if (label === "Blocked") return "blocked";
  if (label === "Needs review") return "review";
  if (label === "Ready") return "ready";
  if (label === "Planned") return "planned";
  return "progress";
}

export function worklistActionLabel(record) {
  if (!record) return "Open";
  const stage = pipelineStage(record);
  const status = String(record.status || "").toLowerCase();
  if (stage === "idea" && status !== "in_progress" && status !== "blocked" && status !== "qa") return "Open";
  return "Continue";
}

export function relativeUpdated(record, now = Date.now()) {
  const raw = record?.updated_at || record?.updatedAt || record?.created_at || record?.createdAt || "";
  if (!raw) return "—";
  const then = new Date(raw).getTime();
  if (!Number.isFinite(then)) return "—";
  const minutes = Math.max(0, Math.round((now - then) / 60000));
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 14) return `${days}d ago`;
  return new Date(then).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function downloadJsonFile(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function copyText(value) {
  const text = String(value || "");
  if (!text) return false;
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  return false;
}

function artifactIsImage(artifact) {
  if (!artifact || typeof artifact !== "object") return false;
  if (isImageArtifact(artifact)) return true;
  return /\.(png|jpe?g|webp|gif)$/i.test(String(artifact.filename || ""));
}

function generationPreviewPath(businessKey, recordId, generationId) {
  return {
    path: `/businesses/${businessKey}/records/${recordId}/generations/${encodeURIComponent(generationId)}/preview`,
    externalUrl: null,
  };
}

function previewFromGeneration(businessKey, recordId, row) {
  if (!row?.id) return null;
  const artifact = row.artifact;
  if (artifact?.source === "link" && artifact.url && artifactIsImage(artifact)) {
    return { path: null, externalUrl: artifact.url };
  }
  if (artifact && !artifactIsImage(artifact)) return null;
  return generationPreviewPath(businessKey, recordId, row.id);
}

export function recordPreviewPlan(businessKey, record) {
  const images = [];
  const seen = new Set();
  const push = (item) => {
    if (!item || (!item.path && !item.externalUrl)) return;
    const key = item.path || item.externalUrl;
    if (seen.has(key)) return;
    seen.add(key);
    images.push(item);
  };
  if (record?.id && businessKey) {
    const byId = new Map(generations(record).map((row) => [row.id, row]));
    const currentId = currentGenerationId(record);
    const current = currentId ? byId.get(currentId) : null;
    if (current) push(previewFromGeneration(businessKey, record.id, current));
    else if (currentId) push(generationPreviewPath(businessKey, record.id, currentId));
    [...deskIterations(record)].reverse().forEach((row) => push(previewFromGeneration(businessKey, record.id, row)));
    [...deskSources(record), ...deskReferences(record)].forEach((row) => push(previewFromGeneration(businessKey, record.id, row)));
    const artifact = designArtifact(record);
    if (artifact && artifactIsImage(artifact)) {
      if (artifact.source === "link" && artifact.url) push({ path: null, externalUrl: artifact.url });
      else push({ path: `/businesses/${businessKey}/records/${record.id}/design-preview`, externalUrl: null });
    }
  }
  const mesh = meshSessionSource(record);
  return {
    images,
    mesh: mesh ? { filename: mesh.filename || "3D file", format: mesh.format || "" } : null,
  };
}

export function resolveRecordPreview(businessKey, record) {
  const first = recordPreviewPlan(businessKey, record).images[0];
  if (!first) return { path: null, externalUrl: null };
  return { path: first.path, externalUrl: first.externalUrl };
}

export function hasRecordPreview(record) {
  return recordPreviewPlan(record?.business_key || "linkforge", record).images.length > 0;
}

const PAST_RENDER_STAGES = new Set([
  "render",
  "ai_studio",
  "studio_return",
  "orca",
  "orca_bridge",
  "customize",
  "gcode",
  "next_cook",
  "prototype",
]);

export function nextPathwayAction(record) {
  if (!record) {
    return {
      id: "create",
      label: "Create product",
      hint: "Start Idea → Design Desk → Render → Google AI Studio → Orca → Orca Bridge check → AI Customize G-code → SD-ready",
      step: "idea",
    };
  }
  const artifact = designArtifact(record);
  const stage = pathwayStage(record);
  const meshSource = meshSessionSource(record);
  if (!artifact && !generations(record).length && !meshSource) {
    return {
      id: "design",
      label: "Open Design Desk",
      hint: "Prompt a render and iterate",
      step: "design",
    };
  }
  const inLoop = Boolean(
    aiStudioPackage(record)
    || latestStudioReturn(record)
    || orcaHandoff(record)
    || orcaBridgeRuns(record).length
    || customizeRuns(record).length
    || gcodeDrops(record).length,
  );
  const pastRender = PAST_RENDER_STAGES.has(stage)
    || inLoop
    || record.area === "prototype"
    || record.area === "qa"
    || record.area === "ready_for_store"
    || record.status === "store_ready";
  if (!pastRender) {
    if (meshSource && !generations(record).length && !isImageArtifact(artifact)) {
      return {
        id: "orca",
        label: "Send to Orca",
        hint: "The session started from a 3D file. Hand that mesh to OrcaSlicer with the low-waste purge recipe.",
        step: "orca",
      };
    }
    return {
      id: "render",
      label: "Advance to Render",
      hint: "Open the stored design preview",
      step: "render",
    };
  }
  if (!aiStudioPackage(record)) {
    return {
      id: "ai_studio",
      label: "Handoff to AI Studio",
      hint: "Export a Google AI Studio (aistudio / Gemini) package: prompt, reference ids, color/plate and purge notes",
      step: "ai_studio",
    };
  }
  if (loopNeedsStudioReturn(record)) {
    return {
      id: "studio_return",
      label: "Return from Studio",
      hint: "Import notes + files onto this record so Design Desk ↔ Google AI Studio never stalls",
      step: "ai_studio",
    };
  }
  const orca = orcaHandoff(record);
  if (!orca || !(orca.files || []).length) {
    return {
      id: "orca",
      label: "Send to Orca",
      hint: "Attach print-ready 3MF/STL plus color notes for OrcaSlicer",
      step: "orca",
    };
  }
  if (!orcaBridgeChecked(record) && !loopSdReady(record)) {
    return {
      id: "orca_bridge",
      label: "Orca Bridge check",
      hint: "Upload the sliced .gcode.3mf. CHECKED means package checks passed, not physical printer validation. Purge reduction stays off. This is not the AI Customize purge optimizer.",
      step: "orca_bridge",
    };
  }
  if (!loopSdReady(record)) {
    return {
      id: "customize",
      label: "AI Customize G-code",
      hint: "The founder low-waste purge recipe is already on the Orca handoff as project settings. Running customize on raw G-code copies the file and does not rewrite toolpaths.",
      step: "customize",
    };
  }
  return {
    id: "next_cook",
    label: "Keep cooking: next cook",
    hint: "An SD-ready file is on the record. Bounce to Design Desk, AI Studio, Orca, or customize again — no dead stage",
    step: "gcode",
  };
}

export function gcodeCandidates(record) {
  const rows = [];
  const seen = new Set();
  const push = (artifact, label) => {
    if (!artifact?.id || seen.has(artifact.id)) return;
    const name = String(artifact.filename || "");
    const looksLikeGcode = artifact.kind === "gcode" || /\.(gcode|gco|bgcode)$/i.test(name);
    if (!looksLikeGcode) return;
    seen.add(artifact.id);
    rows.push({ id: artifact.id, filename: name || "gcode", label });
  };
  for (const file of orcaHandoff(record)?.files || []) push(file, "Orca");
  for (const ret of studioReturns(record)) {
    for (const file of ret.files || []) push(file, "Studio return");
  }
  for (const drop of gcodeDrops(record)) push(drop.artifact, "G-code");
  for (const run of customizeRuns(record)) {
    push(run.input_artifact, "Customize input");
    push(run.output_artifact, "Customize output");
  }
  return rows;
}

function looksLikeSlicedPackage(artifact) {
  return /\.(gcode\.3mf|3mf)$/i.test(String(artifact?.filename || ""));
}

export function slicedPackageCandidates(record) {
  const rows = [];
  const seen = new Set();
  const push = (artifact, label) => {
    if (!artifact?.id || seen.has(artifact.id) || !looksLikeSlicedPackage(artifact)) return;
    seen.add(artifact.id);
    rows.push({ id: artifact.id, filename: artifact.filename, label });
  };
  for (const file of orcaHandoff(record)?.files || []) push(file, "Orca");
  for (const ret of studioReturns(record)) {
    for (const file of ret.files || []) push(file, "Studio return");
  }
  for (const run of orcaBridgeRuns(record)) {
    push(run.input_artifact, "Bridge input");
    push(run.output_artifact, "Bridge checked");
  }
  return rows;
}

export function leadCookingRecord(records) {
  return (records || [])[0] || null;
}
